import { Link } from "react-router-dom";
import { Check, ExternalLink, ArrowRight, Clock, DollarSign } from "lucide-react";

export function AuthorityPhase({ phase, checked, onToggle, extras = {} }) {
  const done = phase.items.filter((i) => checked[i.id]).length;
  const complete = done === phase.items.length;
  return (
    <section className="relative" data-testid={`authority-phase-${phase.id}`}>
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div className={`text-[11px] uppercase tracking-widest font-bold mb-1 ${complete ? "text-emerald-400" : "text-[#FF5A1F]"}`}>
            {phase.label}
          </div>
          <h2 className="text-xl md:text-2xl font-black tracking-tight">{phase.title}</h2>
          <p className="text-sm text-white/55 mt-1 max-w-xl">{phase.subtitle}</p>
        </div>
        <div className={`shrink-0 text-xs font-bold tabular-nums rounded-full px-3 py-1 border ${complete ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-white/10 bg-white/5 text-white/60"}`} data-testid={`authority-phase-count-${phase.id}`}>
          {done}/{phase.items.length}
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] divide-y divide-white/[0.06] overflow-hidden">
        {phase.items.map((item) => (
          <AuthorityItem key={item.id} item={item} done={!!checked[item.id]} onToggle={() => onToggle(item.id)} extra={extras[item.id]} />
        ))}
      </div>
    </section>
  );
}

function AuthorityItem({ item, done, onToggle, extra }) {
  return (
    <div className={`flex gap-4 p-4 sm:p-5 transition-colors ${done ? "bg-emerald-500/[0.04]" : "hover:bg-white/[0.03]"}`} data-testid={`authority-item-${item.id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={done}
        aria-label={done ? `Mark "${item.title}" not done` : `Mark "${item.title}" done`}
        className={`mt-0.5 h-6 w-6 shrink-0 rounded-md border-2 flex items-center justify-center transition-[background-color,border-color,transform] active:scale-90 ${done ? "bg-emerald-500 border-emerald-500 text-black" : "border-white/25 hover:border-[#FF5A1F]"}`}
        data-testid={`authority-item-toggle-${item.id}`}
      >
        {done && <Check className="w-4 h-4" strokeWidth={3} />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`font-bold leading-snug ${done ? "text-white/50 line-through decoration-white/30" : "text-white"}`}>{item.title}</div>
        <p className={`text-sm mt-1 leading-relaxed ${done ? "text-white/35" : "text-white/60"}`}>{item.why}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5 text-xs">
          <span className="inline-flex items-center gap-1 text-white/50"><DollarSign className="w-3 h-3" />{item.cost}</span>
          <span className="inline-flex items-center gap-1 text-white/50"><Clock className="w-3 h-3" />{item.time}</span>
          <ItemLink item={item} />
        </div>
        {extra}
      </div>
    </div>
  );
}

function ItemLink({ item }) {
  if (!item.link) return null;
  const cls = "inline-flex items-center gap-1 font-bold text-[#FF5A1F] hover:text-[#FF7038] transition-colors";
  if (item.link.startsWith("/")) {
    return <Link to={item.link} className={cls} data-testid={`authority-item-link-${item.id}`}>{item.linkLabel}<ArrowRight className="w-3 h-3" /></Link>;
  }
  return (
    <a href={item.link} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`authority-item-link-${item.id}`}>
      {item.linkLabel}<ExternalLink className="w-3 h-3" />
    </a>
  );
}
